import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, Trash2, Edit2, X, Check, Key, Wrench, Rocket } from 'lucide-react';

const getRoleBadge = (role) => {
  if (role === 'supreme_admin') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-semibold rounded-lg">
        <Shield className="w-3 h-3" />
        Admin Suprem
      </span>
    );
  } else if (role === 'admin') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-lg">
        <Shield className="w-3 h-3" />
        Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-lg">
      Utilizator
    </span>
  );
};

const UserRow = ({
  u,
  currentUser,
  isEditing,
  editForm,
  setEditForm,
  onEdit,
  onCancelEdit,
  onUpdate,
  onDelete,
  onEditPermissions,
  onEditToolsPermissions,
  onToggleDeployAccess,
  loading
}) => {
  if (isEditing) {
    return (
      <div
        className="p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors duration-200"
        data-testid={`user-row-${u.username}`}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground">Editare Utilizator</h4>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={onCancelEdit}
                className="rounded-xl"
                data-testid="edit-user-cancel"
              >
                <X className="w-4 h-4 mr-1" />
                Anulează
              </Button>
              <Button
                size="sm"
                variant="premium"
                onClick={() => onUpdate(u._id)}
                disabled={loading}
                className="rounded-xl"
                data-testid="edit-user-save"
              >
                <Check className="w-4 h-4 mr-1" />
                Salvează
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`edit-full-name-${u._id}`}>Nume complet</Label>
              <Input
                id={`edit-full-name-${u._id}`}
                type="text"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`edit-email-${u._id}`}>Email</Label>
              <Input
                id={`edit-email-${u._id}`}
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`edit-password-${u._id}`}>Parolă nouă (opțional)</Label>
              <Input
                id={`edit-password-${u._id}`}
                type="password"
                placeholder="Lasă gol pentru a păstra parola"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`edit-role-${u._id}`}>Rol</Label>
              <Select
                value={editForm.role}
                onValueChange={(value) => setEditForm({ ...editForm, role: value })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Selectează rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Utilizator</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                  {currentUser.role === 'supreme_admin' && u.role !== 'supreme_admin' && (
                    <SelectItem value="supreme_admin">Admin Suprem</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`edit-active-${u._id}`}
              checked={editForm.is_active}
              onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <Label htmlFor={`edit-active-${u._id}`} className="cursor-pointer">
              Cont activ
            </Label>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors duration-200"
      data-testid={`user-row-${u.username}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center text-primary-foreground font-bold">
            {u.full_name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h4 className="font-semibold text-foreground">{u.full_name}</h4>
            <p className="text-sm text-muted-foreground">
              @{u.username} • {u.email}
            </p>
            {u.created_by && (
              <p className="text-xs text-muted-foreground mt-1">
                Creat de: {u.created_by}
              </p>
            )}
            {u.is_active === false && (
              <p className="text-xs text-destructive mt-1">
                Cont inactiv
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {getRoleBadge(u.role)}
          {currentUser.role === 'supreme_admin' && u.username !== currentUser.username && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400"
                onClick={() => onEditPermissions(u)}
                title="Gestionează permisiuni Company Area"
                data-testid={`btn-company-perms-${u.username}`}
              >
                <Key className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-xl hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-600 dark:hover:text-amber-400"
                onClick={() => onEditToolsPermissions(u)}
                title="Gestionează permisiuni Tools Area"
                data-testid={`btn-tools-perms-${u.username}`}
              >
                <Wrench className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`rounded-xl ${
                  u.has_deploy_access 
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' 
                    : 'hover:bg-orange-100 dark:hover:bg-orange-900/30 hover:text-orange-600 dark:hover:text-orange-400'
                }`}
                onClick={() => onToggleDeployAccess(u._id, u.has_deploy_access)}
                title={u.has_deploy_access ? 'Revocă acces Deploy' : 'Acordă acces Deploy'}
                data-testid={`btn-deploy-${u.username}`}
              >
                <Rocket className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-xl hover:bg-accent hover:text-accent-foreground"
                onClick={() => onEdit(u)}
                title="Editează utilizator"
                data-testid={`btn-edit-${u.username}`}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-xl hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => onDelete(u._id, u.username)}
                title="Șterge utilizator"
                data-testid={`btn-delete-${u.username}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserRow;
